import { CdkFixedSizeVirtualScroll, CdkVirtualForOf, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { ChangeDetectorRef, Component, forwardRef, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatOptionModule, MatOptionSelectionChange } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { combineLatest, debounceTime, filter, map, Observable, of, Subscription, switchMap, tap } from 'rxjs';
import { City } from 'src/app/api/v1/models';
import { Street } from 'src/app/api/v2/models';
import { FormSaveStrategy } from 'src/app/layout/common/strategy/form-save-strategy/form-save-strategy.abstract';
import { CitiesService } from 'src/app/services/cities.service';
import { FormStepAbstract } from 'src/app/shared/components/abstract/form-step.abstract';
import { EzInputComponent } from 'src/app/shared/components/ez-input/ez-input.component';
import { EzInputDirective } from 'src/app/shared/components/ez-input/ez-input.directive';
import { FormType } from 'src/app/shared/interfaces/form-type';
import { FilterPipe } from 'src/app/shared/pipes/filter/filter.pipe';
import { SortStringPipe } from 'src/app/shared/pipes/sort-string/sort-string.pipe';
import { SessionRepository } from 'src/app/shared/repositories/session.repository';
import { FlatValidator } from 'src/app/shared/validators/text.validators';
import { AsyncPipe, NgClass } from '@angular/common';

interface IZoneAddressControl {
  city: string | null,
  street: string | null,
  house: string | null,
  flat: string | null,
}

interface ICityStreet {
  city: City,
  street: Street,
}

@UntilDestroy()
@Component({
  selector: 'app-zone-address-step',
  templateUrl: './zone-address-step.component.html',
  styleUrls: ['./zone-address-step.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    NgxMatSelectSearchModule,
    CdkVirtualScrollViewport,
    CdkFixedSizeVirtualScroll,
    CdkVirtualForOf,
    FilterPipe,
    SortStringPipe,
    EzInputComponent,
    EzInputDirective,
    NgClass,
    AsyncPipe,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ZoneAddressStepComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ZoneAddressStepComponent),
      multi: true,
    }
  ]
})
export class ZoneAddressStepComponent extends FormStepAbstract implements OnInit {
  private citiesService: CitiesService = inject(CitiesService);
  private sessionRepository: SessionRepository = inject(SessionRepository);
  private formSaveStrategy: FormSaveStrategy = inject(FormSaveStrategy);
  private changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);

  private deliverySub: Subscription;
  private addressSub: Subscription;

  override form: FormGroup<FormType<IZoneAddressControl>> = new FormGroup<FormType<IZoneAddressControl>>({
    city: new FormControl('', {nonNullable: true, validators: [Validators.required]}),
    street: new FormControl('', {nonNullable: true, validators: [Validators.required]}),
    house: new FormControl('', {nonNullable: true, validators: [Validators.maxLength(100), Validators.required]}),
    flat: new FormControl('', {
      validators: [Validators.maxLength(15), FlatValidator, Validators.required],
      nonNullable: true
    }),
  });

  isLoadingCities$: Observable<boolean> = this.citiesService.isLoading$;
  cityStreets$: Observable<ICityStreet[] | null> = this.citiesService.cities$.pipe(
    tap(() => {
      this.subscribeToDeliveryAddress();
    }),
    map(cities =>
      cities?.reduce(
        (streets, currentCity) => {
          currentCity.streets.forEach(street => streets.push({city: currentCity, street}));
          return streets;
        },
        [] as ICityStreet[]
      ) ?? null),
  );

  override ngOnInit(): void {
    super.ngOnInit();
    this.subscribeToDeliveryZone();
  }

  private subscribeToDeliveryAddress(): void {
    this.addressSub?.unsubscribe();

    this.addressSub = this.sessionRepository.deliveryAddress$.pipe(
      tap(deliveryAddress => {
        if(!deliveryAddress) {
          return;
        }
        this.form.patchValue(deliveryAddress, {emitEvent: false});
        this.changeDetectorRef.detectChanges();
      }),
      untilDestroyed(this)
    ).subscribe();
  }

  private subscribeToDeliveryZone(): void {
    this.deliverySub?.unsubscribe();
    this.deliverySub = combineLatest([
      this.form.controls.street.valueChanges || of(null),
      this.form.controls.house.valueChanges || of(null),
      this.form.controls.flat.valueChanges || of(null),
    ]).pipe(
      debounceTime(this.formSaveStrategy.debounceTimeout),
      map(() => this.form.getRawValue()),
      filter(formValue => !!formValue && !!formValue.street && !!formValue.house && !!formValue.flat),
      switchMap(formValue => this.formSaveStrategy.setDeliveryAddress(formValue)),
      tap((formValue) => {
        this.citiesService.setDeliveryAddress(formValue);
      }),
      untilDestroyed(this),
    ).subscribe();
  }

  onChangeStreet($event: MatOptionSelectionChange, cityStreet: ICityStreet): void {
    if(!$event.isUserInput) {
      return;
    }
    this.form.controls.city.setValue(cityStreet.city.name || '', {emitEvent: false});
  }
}
